/**
 * Registry of in-app feature hints and multi-step flows ("things moved"
 * spotlights and guided tours).
 *
 * A hint is a one-step anchored popover pointing at a UI element that changed.
 * A flow is an ordered chain of steps, each anchored on its own page: the user
 * clicks the highlighted element (or a Next button) to advance, and the final
 * "Got it" dismisses the whole flow. Per-user seen/dismissed state is stored
 * server-side (user_hints table, one key per hint or flow) and hydrated via
 * the session payload; mid-flow progress is kept in localStorage so a refresh
 * resumes where the user left off.
 *
 * Adding a hint or flow = adding an entry here. No migration needed.
 */

/**
 * Who a hint/flow targets, evaluated against the user's created-at date:
 * - 'existing-users': created before shippedAt (they knew the old UI — "this moved")
 * - 'new-users': created on/after shippedAt (onboarding — "start here")
 * - 'all': everyone
 */
export type HintAudience = 'existing-users' | 'new-users' | 'all';

export type HintLocation = 'top' | 'bottom' | 'start' | 'end';

export interface FeatureHint {
    /** Stable unique key, persisted server-side. Convention: <slug>-<yyyy-mm>. */
    key: string;
    /** Route name the hint appears on. */
    routeName: string;
    /** CSS selector for the element the hint points at. */
    target: string;
    title: string;
    body: string;
    /** Popover placement relative to the target. */
    location: HintLocation;
    /** Defaults to 'existing-users' (the "this moved" case). */
    audience?: HintAudience;
    /** Reference date for the audience gate (see HintAudience). */
    shippedAt: string;
    /** Hint auto-retires after this date. */
    expiresAt: string;
}

export interface FeatureFlowStep {
    /** Route name this step appears on. Steps only render when their route is active. */
    routeName: string;
    /** CSS selector for the element the step points at. */
    target: string;
    title: string;
    body: string;
    location: HintLocation;
    /**
     * How the flow advances past this step:
     * - 'target': the user clicks the highlighted element (e.g. a nav link or
     *   button). Landing on a later step's route also advances, so if the
     *   click navigates, the next step appears on the new page.
     * - 'button': the popover shows a Next button (use when the step is
     *   informational and doesn't require an action).
     * The last step always shows "Got it", which dismisses the whole flow.
     */
    advanceOn: 'target' | 'button';
}

export interface FeatureFlow {
    /** Stable unique key for the WHOLE flow, persisted server-side. */
    key: string;
    audience: HintAudience;
    shippedAt: string;
    expiresAt: string;
    /** Ordered steps; each may live on a different page. */
    steps: FeatureFlowStep[];
}

export const featureHints: FeatureHint[] = [
    {
        key: 'esign-editor-intro-2026-07',
        routeName: 'documentsv2.fields',
        target: '[data-hint="esign-fields-panel"]',
        title: 'Build your template here',
        body: 'Click a field to add it to the document, then drag it into place. Start with a Signature field.',
        location: 'end',
        audience: 'all',
        shippedAt: '2026-07-22T00:00:00Z',
        expiresAt: '2028-01-01T00:00:00Z',
    },
    {
        key: 'templates-moved-2026-07',
        routeName: 'documents.index',
        target: '[data-hint="contracts-templates-button"]',
        title: 'Templates moved',
        body: 'Manage your document templates from here — they create the contracts listed below.',
        location: 'bottom',
        shippedAt: '2026-07-22T00:00:00Z',
        expiresAt: '2026-09-30T00:00:00Z',
    },
    {
        key: 'order-auto-extend-2026-07',
        routeName: 'orders.single',
        target: '[data-hint="order-auto-extend"]',
        title: 'New: Auto-Extend',
        body: 'Keep a rental rolling automatically — Auto-Extend renews it on a schedule and bills each period, so open-ended and gig-driver rentals run themselves.',
        location: 'bottom',
        audience: 'all',
        shippedAt: '2026-07-23T00:00:00Z',
        expiresAt: '2026-12-31T00:00:00Z',
    },
];

export const featureFlows: FeatureFlow[] = [
    /**
     * LIVE DEMO — comment this whole entry out once you've seen it work.
     *
     * Walks: dashboard ("theme your checkout page") → click Settings in the
     * main nav → "Booking page style" in the settings menu → the theme
     * customizer, where "Got it" dismisses the whole flow under one key.
     *
     * To see it again after dismissing, delete your row:
     *   php artisan tinker --execute="App\Models\UserHint::where('hint_key', 'theme-checkout-demo-2026-07')->delete();"
     * and hard-refresh the dashboard (the dismissed list rides on the session payload).
     *
     * Note: step 2's target only exists if your account has the store-themes
     * feature (it gates the "Booking page style" menu item).
     */
    {
        key: 'theme-checkout-demo-2026-07',
        audience: 'all',
        shippedAt: '2026-07-01T00:00:00Z',
        expiresAt: '2026-12-31T00:00:00Z',
        steps: [
            {
                routeName: 'dashboard',
                target: '[data-hint="nav-settings"]',
                title: 'New: theme your checkout page',
                body: 'You can now style your customer-facing booking pages. Head to Settings to try it.',
                location: 'end',
                advanceOn: 'target',
            },
            {
                routeName: 'settings.general',
                target: '[data-hint="nav-booking-page-style"]',
                title: 'Booking page style',
                body: 'Open Booking page style to pick a preset and match your brand.',
                location: 'end',
                advanceOn: 'target',
            },
            {
                routeName: 'themes.index',
                target: '#theme .v-card-title',
                title: 'Customize your customer-facing pages',
                body: 'Pick a preset, tune the accent color, and save. Visitors see it on their next page load.',
                location: 'bottom',
                advanceOn: 'button',
            },
        ],
    },
    /**
     * Example: new-signup setup tour. Uncomment (and adjust targets/dates) to
     * walk fresh accounts through initial setup. Each step waits on its own
     * page; clicking the highlighted element advances, and the final "Got it"
     * marks the whole flow dismissed under the single key below.
     */
    // {
    //     key: 'new-signup-setup-2026-08',
    //     audience: 'new-users',
    //     shippedAt: '2026-08-01T00:00:00Z',
    //     expiresAt: '2027-08-01T00:00:00Z',
    //     steps: [
    //         {
    //             routeName: 'dashboard',
    //             target: '[data-hint="nav-settings"]',
    //             title: 'Set up taxes first',
    //             body: 'Head to Settings to configure your tax rates before your first booking.',
    //             location: 'end',
    //             advanceOn: 'target',
    //         },
    //         {
    //             routeName: 'settings.taxes',
    //             target: '[data-hint="add-tax-button"]',
    //             title: 'Add your tax rate',
    //             body: 'Create the tax rate that applies to your rentals. You can add more later.',
    //             location: 'bottom',
    //             advanceOn: 'button',
    //         },
    //         {
    //             routeName: 'listing.index',
    //             target: '[data-hint="new-listing-button"]',
    //             title: 'Add your first listing',
    //             body: 'Create a listing for one of your vehicles to start taking bookings.',
    //             location: 'bottom',
    //             advanceOn: 'target',
    //         },
    //         {
    //             routeName: 'listing.new',
    //             target: '[data-hint="listing-extras"]',
    //             title: 'Offer extras',
    //             body: 'Add-ons like child seats or prepaid fuel boost your revenue per booking.',
    //             location: 'top',
    //             advanceOn: 'button',
    //         },
    //     ],
    // },
];
